import React, { useState, useMemo } from "react";
import { holidayPackages, holidayCategories } from "../mock";
import { Card, CardContent } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Star, Search, Filter } from "lucide-react";

const formatINR = (n) => new Intl.NumberFormat("en-IN").format(n);

export default function Holidays() {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("popular");
  const [category, setCategory] = useState("All");

  const filtered = useMemo(() => {
    let list = holidayPackages.filter((p) => p.title.toLowerCase().includes(query.toLowerCase()));
    if (category !== "All") list = list.filter((p) => p.category === category);
    if (sort === "low") list = [...list].sort((a,b) => a.price - b.price);
    if (sort === "high") list = [...list].sort((a,b) => b.price - a.price);
    if (sort === "rating") list = [...list].sort((a,b) => b.rating - a.rating);
    return list;
  }, [query, sort, category]);

  const categoryCounts = useMemo(() => {
    const counts = { All: holidayPackages.length };
    holidayCategories.forEach((c) => {
      if (c !== "All") counts[c] = holidayPackages.filter((p) => p.category === c).length;
    });
    return counts;
  }, []);

  return (
    <main className="pt-32">
      <section className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="text-xs tracking-[0.3em] text-[#5c8606] font-semibold">HOLIDAYS</div>
        <h1 className="font-display text-5xl md:text-6xl font-bold text-slate-800 mt-3">Curated Holiday Packages</h1>
        <p className="mt-4 text-lg text-slate-600 max-w-2xl">Handpicked itineraries — from honeymoon getaways to family escapes and once-in-a-lifetime adventures.</p>

        <div className="mt-8 flex flex-wrap gap-2">
          {holidayCategories.map((c) => (
            <button
              key={c}
              onClick={() => setCategory(c)}
              className={`text-sm px-4 py-2 rounded-full font-semibold border transition-colors ${
                category === c
                  ? "bg-slate-800 text-white border-slate-800"
                  : "bg-white text-slate-600 border-slate-200 hover:border-slate-300"
              }`}
            >
              {c} <span className={`ml-1 text-xs ${category === c ? "text-slate-300" : "text-slate-400"}`}>({categoryCounts[c]})</span>
            </button>
          ))}
        </div>

        <div className="mt-4 flex flex-col md:flex-row gap-3 md:items-center bg-white p-3 rounded-2xl border border-slate-100 shadow-sm">
          <div className="flex-1 relative">
            <Search className="w-4 h-4 absolute left-4 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search destinations, packages..." className="pl-11 h-12 border-slate-200 rounded-xl" />
          </div>
          <div className="flex items-center gap-2">
            <Filter className="w-4 h-4 text-slate-500" />
            {[
              { k: "popular", label: "Popular" },
              { k: "low", label: "Price: Low to High" },
              { k: "high", label: "Price: High to Low" },
              { k: "rating", label: "Top Rated" },
            ].map((s) => (
              <button key={s.k} onClick={() => setSort(s.k)} className={`text-xs px-3 py-2 rounded-full font-semibold transition-colors ${sort === s.k ? "bg-[#70a308] text-white" : "bg-slate-100 text-slate-600 hover:bg-slate-200"}`}>{s.label}</button>
            ))}
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 lg:px-10 mt-10">
        {filtered.length === 0 && (
          <div className="text-center py-20 text-slate-500">
            No packages match your search. Try a different keyword or category.
          </div>
        )}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filtered.map((p) => (
            <Card key={p.id} className="lift overflow-hidden rounded-2xl border-slate-100">
              <div className="relative h-52 overflow-hidden bg-gradient-to-br from-[#f9fcf3] to-slate-200">
                <img src={p.image} alt={p.title} loading="lazy" onError={(e) => { e.currentTarget.onerror = null; e.currentTarget.style.display = "none"; }} className="w-full h-full object-cover hover:scale-110 transition-transform duration-500" />
                <Badge className="absolute top-3 left-3 bg-white text-slate-800 hover:bg-white rounded-full">
                  <Star className="w-3 h-3 fill-yellow-400 text-yellow-400 mr-1" /> {p.rating}
                </Badge>
              </div>
              <CardContent className="p-5">
                <h4 className="font-bold text-slate-800 text-lg line-clamp-2 min-h-[56px]">{p.title}</h4>
                <p className="text-xs text-slate-500 mt-1">{p.duration}</p>
                <div className="flex flex-wrap gap-1.5 mt-3">
                  {p.features.map((f) => (
                    <span key={f} className="text-[10px] px-2 py-1 rounded-full bg-[#f9fcf3] text-[#5c8606] font-medium">{f}</span>
                  ))}
                </div>
                <ul className="mt-3 space-y-1">
                  {p.highlights.map((h,i) => (
                    <li key={i} className="text-xs text-slate-600 flex gap-1.5"><span className="text-[#70a308]">•</span>{h}</li>
                  ))}
                </ul>
                <div className="mt-4 pt-4 border-t border-slate-100 flex items-center justify-between">
                  <div>
                    <span className="text-xs text-slate-400 line-through">INR {formatINR(p.oldPrice)}</span>
                    <div className="text-xl font-bold text-slate-800">INR {formatINR(p.price)}</div>
                  </div>
                  <Button className="bg-gradient-to-r from-[#84c009] to-[#4f7700] hover:from-[#70a308] hover:to-[#416200] text-white rounded-full h-10 px-5 font-semibold">Book Now</Button>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </section>
    </main>
  );
}
